import { FeedCollection, RandomPost } from '../schema-copy/feed-schema';
import {
  putIntoQueryCache, getFromQueryCache, putPromiseIntoQueryCache,
  removeFromQueryCache, isPromise,
} from 't-lib-ui/connector';
import { parseFeedCollection, getArchiveNameForIndex } from './post-engine';
import { getFeedNameOverride } from './feed-name-overrides';
import { getAuthFromStorage, getCdnUrl } from './auth';

export const RANDOM_FEED_ID = 'random';
const RANGES_KEY = 'random-feed-ranges';
const CACHE_MAX_AGE_MS = 3_600_000;
const MAX_ARCHIVE_FETCHES = 15; // cap CDN downloads per session — give up after this many archives
const MAX_RENDER_RANGES = 8;
const BATCH_SIZE = 60; // post tuples returned per getRandomPostTuples() call (~3 archives)

interface RenderedRange { low: number; high: number; }

interface RandomState {
  baseUrl: string;
  totalPostCount: number;   // lastPost + 1 from CDN
  firstLoadedIndex: number; // lowest post index loaded into cache
  archivesFetched: number;  // count of pN.dz files fetched (excludes current.dz)
}

let state: RandomState | null = null;
let initPromise: Promise<RandomState | null> | null = null;
// Highest post index shown in the current session (set on the first, cursor-less
// getRandomPostTuples call when the feed is opened). markRandomPostRendered marks up
// to this — the top of what the user actually saw — instead of the absolute newest
// index, so reaching a post can't mark never-shown newer posts as seen.
let sessionTopIndex: number | null = null;
// Serializes archive downloads so concurrent callers don't double-count
// archivesFetched or fight over firstLoadedIndex. The actual CDN fetch is also
// deduplicated by the query cache, so the network request never happens twice.
let archiveLoadChain: Promise<unknown> = Promise.resolve();

let cachedRanges: RenderedRange[] | null = null;

function getRanges(): RenderedRange[] {
  if (cachedRanges) return cachedRanges;
  try {
    const s = localStorage.getItem(RANGES_KEY);
    if (s) {
      const parsed = JSON.parse(s);
      console.debug('Parsed random feed rendered ranges from localStorage:', parsed);
      if (Array.isArray(parsed)) {
        cachedRanges = parsed;
        return cachedRanges;
      }
    }
  } catch (e) {
    console.error('Error parsing random feed ranges from localStorage:', e);
  }
  cachedRanges = [];
  return cachedRanges;
}

function saveRanges(ranges: RenderedRange[]) {
  cachedRanges = ranges;
  console.debug('Saved random feed rendered ranges:', ranges);
  try { localStorage.setItem(RANGES_KEY, JSON.stringify(ranges)); } catch (e) {
    console.error('Error saving random feed ranges to localStorage:', e);
  }
}

function getDistanceBetweenRanges(a: RenderedRange, b: RenderedRange): number {
  if (a.high <= b.low) return b.low - a.high;
  if (b.high <= a.low) return a.low - b.high;
  return 0;
}

// Range that already covers post `i` (so it has been rendered and should be skipped).
function rangeContaining(i: number): RenderedRange | undefined {
  for (const r of getRanges())
    if (i >= r.low && i < r.high) return r;
  return undefined;
}

export function markRandomPostRendered(postIndex: number) {
  // Posts are shown newest-first (highest index at the top) and the user scrolls
  // strictly downward, so reaching postIndex means every higher-numbered post above
  // it — up to the top of what was shown this session — has been scrolled past and
  // seen. Mark that whole span: this still covers the most-visible top posts (which
  // never fire their own visible-change event, since PostRenderer only emits one on
  // scroll, not for the initially-shown post) without reaching past the session's
  // first post into never-shown newer posts.
  const top = (sessionTopIndex ?? state?.totalPostCount ?? postIndex) + 1;
  const newRange: RenderedRange = { low: postIndex, high: Math.max(top, postIndex + 1) };
  const ranges = [...getRanges()];

  // Insert keeping sorted ascending by .low
  let insertAt = 0;
  while (insertAt < ranges.length && ranges[insertAt].low < newRange.low) insertAt++;
  ranges.splice(insertAt, 0, newRange);

  // Eagerly merge any adjacent or overlapping neighbours (distance == 0).
  for (let j = 0; j < ranges.length - 1;) {
    if (getDistanceBetweenRanges(ranges[j], ranges[j + 1]) == 0) {
      ranges[j] = {
        low: Math.min(ranges[j].low, ranges[j + 1].low),
        high: Math.max(ranges[j].high, ranges[j + 1].high),
      };
      ranges.splice(j + 1, 1);
    } else {
      j++;
    }
  }

  // If still over the cap, repeatedly merge the closest neighbouring pair.
  while (ranges.length > MAX_RENDER_RANGES) {
    let bestIdx = 0;
    let bestDist = Infinity;
    for (let j = 0; j < ranges.length - 1; j++) {
      const d = getDistanceBetweenRanges(ranges[j], ranges[j + 1]);
      if (d < bestDist) { bestDist = d; bestIdx = j; }
    }
    ranges[bestIdx] = {
      low: Math.min(ranges[bestIdx].low, ranges[bestIdx + 1].low),
      high: Math.max(ranges[bestIdx].high, ranges[bestIdx + 1].high),
    };
    ranges.splice(bestIdx + 1, 1);
  }

  saveRanges(ranges);
}

// Fetch one CDN collection file (current.dz or pN.dz) and cache every post under
// [RANDOM_FEED_ID, number] so PostRenderer can read them straight from the cache.
// The in-flight promise is stored under a file key first, so a second caller for
// the same file reuses it instead of issuing a duplicate download.
async function fetchAndCache(baseUrl: string, fileName: string): Promise<FeedCollection> {
  const fileKey = [RANDOM_FEED_ID, `file:${fileName}`];
  const cached = getFromQueryCache<FeedCollection>(fileKey, CACHE_MAX_AGE_MS);
  if (cached) return isPromise(cached) ? cached : cached.data;

  const promise = (async () => {
    const res = await fetch(`${baseUrl}/${fileName}`);
    if (!res.ok) throw new Error(`Random feed ${fileName}: ${res.status}`);
    return parseFeedCollection(await res.arrayBuffer());
  })();
  putPromiseIntoQueryCache<FeedCollection>(fileKey, promise, CACHE_MAX_AGE_MS);

  const coll = await promise;
  for (const post of coll.posts) {
    const rp = post as RandomPost;
    const custom = getFeedNameOverride(rp.feedId);
    if (custom) rp.feedName = custom;
    putIntoQueryCache([RANDOM_FEED_ID, post.number.toString()], rp, CACHE_MAX_AGE_MS);
  }
  // Individual posts are cached now; the collection wrapper is no longer needed.
  removeFromQueryCache(fileKey);
  return coll;
}

// Load current.dz once and build the initial state. Concurrent first calls share
// the same promise; a failed init clears it so a later call can retry.
async function ensureInitialized(): Promise<RandomState | null> {
  if (state) return state;
  if (!initPromise) {
    initPromise = (async () => {
      // The random feed is public CDN content; getCdnUrl falls back to the global CDN base when
      // there's no auth, so this works in anonymous mode too.
      const baseUrl = `${getCdnUrl(getAuthFromStorage()).replace(/\/+$/, '')}/random`;
      const timeBucket = Math.floor(Date.now() / (15 * 60 * 1000));
      const current = await fetchAndCache(baseUrl, `current.dz?t=${timeBucket}`);
      state = {
        baseUrl,
        totalPostCount: current.lastPost + 1,
        firstLoadedIndex: current.firstPost,
        archivesFetched: 0,
      };
      console.log(`Random feed initialized: total posts=${state.totalPostCount}, first loaded index=${state.firstLoadedIndex}`);
      return state;
    })().catch(err => {
      initPromise = null; // allow a retry on the next call
      throw err;
    });
  }
  return initPromise;
}

// Fetch the archive containing `index`, lowering firstLoadedIndex to its first post.
// The walk skips rendered runs before asking to load, so every archive between the
// current floor and `index` holds only already-seen posts — we jump straight to
// index's archive and never download those. Returns false when the archive cap is
// reached. Serialized via the chain so concurrent callers don't double-count or
// re-download (the CDN fetch is also deduplicated by the query cache).
function loadArchiveFor(st: RandomState, index: number): Promise<boolean> {
  const run = archiveLoadChain.then(async () => {
    if (index >= st.firstLoadedIndex) return true; // already covered by a prior load
    if (st.archivesFetched >= MAX_ARCHIVE_FETCHES) return false;
    const archive = await fetchAndCache(st.baseUrl, getArchiveNameForIndex(Math.max(index, 0)));
    st.firstLoadedIndex = archive.firstPost;
    st.archivesFetched++;
    return true;
  });
  archiveLoadChain = run.catch(() => {});
  return run;
}

/**
 * Returns the next page of random post tuples plus a cursor to continue from.
 *
 * Call without a cursor to start (loads current.dz on first use); pass the returned
 * cursor to fetch the following page. Archives are downloaded lazily, only as far as
 * needed to fill the page, and posts already marked rendered are skipped. An empty
 * `posts` array means there are no more posts to show (feed exhausted or archive cap
 * reached); the cursor can still be passed back, it will keep returning empty.
 */
export async function getRandomPostTuples(cursor?: string): Promise<{ cursor: string; posts: [string, number][] }> {
  const st = await ensureInitialized();
  if (!st) return { cursor: cursor ?? '', posts: [] };

  // A cursor-less call means the feed was just opened: start a fresh session.
  if (cursor == undefined) sessionTopIndex = null;

  let i = cursor != undefined ? parseInt(cursor, 10) : st.totalPostCount - 1;
  if (isNaN(i)) i = st.totalPostCount - 1;

  const posts: [string, number][] = [];
  while (posts.length < BATCH_SIZE && i >= 0) {
    const r = rangeContaining(i);
    if (r) { i = r.low - 1; continue; }          // skip an already-rendered run
    if (i < st.firstLoadedIndex) {
      if (!await loadArchiveFor(st, i)) break;    // archive cap reached, stop here
      continue;                                   // firstLoadedIndex dropped, re-check i
    }
    posts.push([RANDOM_FEED_ID, i]);
    i--;
  }
  // Remember the highest index shown this session (posts are descending, so the
  // first one is the highest). Kept across load-more calls via Math.max.
  if (posts.length) sessionTopIndex = Math.max(sessionTopIndex ?? -1, posts[0][1]);
  if (!posts.length) {
    // Exhausted: nothing left to show. Dump the rendered-range "read markers" that
    // caused it so we can tell whether they legitimately cover the whole feed or
    // got over-marked. Ranges are stored half-open [low, high); printed inclusive.
    const ranges = getRanges();
    const seen = ranges.reduce((n, r) => n + (r.high - r.low), 0);
    console.log(
      `[random-feed] exhausted — no unread posts. totalPostCount=${st.totalPostCount}, `
      + `firstLoadedIndex=${st.firstLoadedIndex}, archivesFetched=${st.archivesFetched}, `
      + `startCursor=${cursor ?? '(initial)'}, postsMarkedSeen=${seen}. `
      + `Rendered ranges (${ranges.length}): `
      + (ranges.length ? ranges.map(r => `${r.low}-${r.high - 1}`).join(', ') : '∅')
    );
  }
  return { cursor: String(i), posts };
}

export function getRandomFeedBaseUrl(): string { return state?.baseUrl ?? ''; }
export function getRandomFeedPostCount(): number { return state?.totalPostCount ?? 0; }
